import { ModelState } from "../interfaces/modelState";
import dateUtil from "../lib/dateUtil";
import { get, post } from "../lib/fetchUtil";

export interface FetchPoliciesByAddressRequest {
  postalCode: string;
  houseNumber: string;
  houseNumberAddition: string;
}

export interface FetchPoliciesByPolicyNumberRequest {
  policyNumber: string;
}

export interface FetchPoliciesByPersonRequest {
  inserts: string;
  lastName: string;
  dateOfBirth: string;
}

export type FetchPoliciesResponse = SearchPolicy[] | { modelState: ModelState };

export interface SearchPolicy {
  policyNumber: string;
  insurance: string;
  name: string;
  dateOfBirth: string;
  address: string;
  role: string;
}

export async function fetchPoliciesByAddress(
  request: Partial<FetchPoliciesByAddressRequest>
): Promise<FetchPoliciesResponse> {
  const response = await post(`policy/findByAddress`, request);

  return response.json();
}

export async function fetchPoliciesByPolicyNumber(
  request: Partial<FetchPoliciesByPolicyNumberRequest>
): Promise<FetchPoliciesResponse> {
  const response = await post(`policy/findByPolicyNumber`, request);

  return response.json();
}

export async function fetchPoliciesByPerson(
  request: Partial<FetchPoliciesByPersonRequest>
): Promise<FetchPoliciesResponse> {
  const response = await post(`policy/findByPersonDetails`, {
    inserts: request.inserts,
    lastName: request.lastName,
    dateOfBirth: dateUtil.dutchDateToMoment(request.dateOfBirth).format("YYYY-MM-DD"),
  });

  return response.json();
}

export async function fetchPolicy(policyNr: string): Promise<Policy> {
  const response = await get(`policy/get?policyNumber=${policyNr}`);

  return response.json();
}

export async function fetchDcpPolicy(policyNr: string): Promise<Policy> {
  const response = await get(`policy/dcp?policyNumber=${policyNr}`);

  return response.json();
}

export async function fetchDlpPolicy(policyNr: string): Promise<Policy> {
  const response = await get(`policy/dlp?policyNumber=${policyNr}`);

  return response.json();
}

export interface Premium {
  paymentPeriod: string;
  premium: number;
  actualPeriodIndicator: boolean;
}

interface Premiums {
  premium: Premium[];
}

interface InsuredsOfCover {
  contactId: string;
  coverSmoker: string;
}

interface InsuredsOfCoverList {
  insuredOfCover: InsuredsOfCover[];
}

interface Cover {
  name: string;
  premiumPaymentTerm: string;
  insuredsOfCover: InsuredsOfCoverList;
  expiredOnDate?: string;
  insuredCapital?: number;
  premiumPaymentExpiredOnDate?: string;
  reductionRate?: number;
  premiums: Premiums;
  validity: "Toekomstig" | "Geldig" | "Beeindigd";
}

interface Covers {
  cover: Cover[];
}

interface NaturalPersonSummary {
  dateOfBirth?: string;
  gender: string;
  givenName: string;
  initials: string;
  prefixName: string;
  surName: string;
}

interface PolicyHolder {
  contactId: string;
  naturalPersonSummary: NaturalPersonSummary;
}

interface PolicyHolders {
  policyHolder: PolicyHolder[];
}

interface BusinessRelationSummary {
  name: string;
}

interface PartialGuardian {
  businessRelationSummary: BusinessRelationSummary;
  naturalPersonSummary?: NaturalPersonSummary;
}

interface PartialGuardians {
  partialGuardian: PartialGuardian[];
}

interface Agent {
  status: string;
}

interface Agents {
  agent: Agent[];
}

interface Cumulatives {
  cumulative: Cumulative[];
}

interface Cumulative {
  coverCode: string;
  coverName: string;
  coverValidity: string[];
  totalAnnualPremium: number;
  totalHalfYearlyPremium: number;
  totalInsuredCapital: number;
  totalMonthlyPremium: number;
  totalNonContributoryValue: number;
  totalQuarterlyPremium: number;
}

export interface Insured {
  contactId: string;
  cumulatives: Cumulatives;
  naturalPersonSummary: NaturalPersonSummary;
}

interface Insureds {
  insured: Insured[];
}

interface AreaSEPA {
  iban: string;
}

interface Collection {
  areaSEPA: AreaSEPA;
}

interface Collections {
  collection: Collection[];
}

export interface Policy {
  effectiveDate: string;
  productCode: string;
  productName: string;
  policyNumber: string;
  systemId: string;
  premiums?: Premiums;
  covers?: Covers;
  policyHolders?: PolicyHolders;
  partialGuardians?: PartialGuardians;
  agents: Agents;
  insureds?: Insureds;
  collections?: Collections;
}
